import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Table, Button, Select } from '../ui';

/**
 * AppointmentList component displays a list of appointments with status information and actions
 */
const AppointmentList = ({
  appointments = [],
  onEdit,
  onDelete,
  onStatusChange,
  canManageAppointments
}) => {
  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return '未设置';
    const date = new Date(dateString);

    const dateFormat = new Intl.DateTimeFormat('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });

    const timeFormat = new Intl.DateTimeFormat('zh-CN', {
      hour: '2-digit',
      minute: '2-digit'
    });

    return `${dateFormat.format(date)} ${timeFormat.format(date)}`;
  };

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusClasses = {
      scheduled: 'bg-blue-100 text-blue-800',
      confirmed: 'bg-emerald-100 text-emerald-800',
      completed: 'bg-purple-100 text-purple-800',
      cancelled: 'bg-red-100 text-red-800',
      noshow: 'bg-gray-100 text-gray-800'
    };

    const statusLabels = {
      scheduled: '已预约',
      confirmed: '已签到',
      completed: '已完成',
      cancelled: '已取消',
      noshow: '未到诊'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  // Status dropdown for changing appointment status
  const StatusDropdown = ({ appointment, onStatusChange }) => {
    const handleStatusChange = (e) => {
      onStatusChange(appointment.id, e.target.value);
    };

    return (
      <Select
        value={appointment.status}
        onChange={handleStatusChange}
        size="small"
        options={[
          { value: 'scheduled', label: '已预约' },
          { value: 'confirmed', label: '已签到' },
          { value: 'completed', label: '已完成' },
          { value: 'cancelled', label: '已取消' },
          { value: 'noshow', label: '未到诊' }
        ]}
      />
    );
  };

  // 获取支付信息（取最新归档/已完成的调理记录）
  const getPaymentInfo = (appointment) => {
    if (!appointment.treatmentRecords || appointment.treatmentRecords.length === 0) return { status: '无记录', amount: '-' };
    // 取归档/已完成的最新一条
    const sorted = [...appointment.treatmentRecords].filter(r => r.status === 'archived' || r.status === 'completed').sort((a, b) => new Date(b.date) - new Date(a.date));
    const record = sorted[0] || appointment.treatmentRecords[0];
    if (!record.settlement) return { status: '无结算', amount: '-' };
    const paid = Number(record.settlement.paidAmount || 0);
    const total = Number(record.settlement.totalAmount || 0);
    return {
      status: paid >= total && total > 0 ? '已支付' : '未支付',
      amount: paid > 0 ? paid : '-'
    };
  };

  // Table columns configuration
  const columns = [
    {
      key: 'customer',
      title: '客户信息',
      render: (_, appointment) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center text-white font-semibold shadow-lg">
              {appointment.customerName ? appointment.customerName.charAt(0) : '?'}
            </div>
          </div>
          <div>
            <Link
              to={`/customers/${appointment.customerId}`}
              className="text-sm font-medium text-gray-900 hover:text-emerald-600 transition-colors duration-200"
            >
              {appointment.customerName || '未知客户'}
            </Link>
            {appointment.customerPhone && (
              <p className="text-xs text-gray-500 mt-1">
                {appointment.customerPhone}
              </p>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'treatment',
      title: '调理项目',
      render: (_, appointment) => (
        <div>
          <div className="text-sm text-gray-900 font-medium">
            {appointment.treatmentName || '未指定项目'}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {appointment.duration} 分钟
          </div>
        </div>
      )
    },
    {
      key: 'practitioner',
      title: '调理师',
      render: (_, appointment) => (
        <div className="text-sm text-gray-900">
          {appointment.practitionerName || '未指定调理师'}
        </div>
      )
    },
    {
      key: 'time',
      title: '时间',
      render: (_, appointment) => (
        <div className="text-sm text-gray-900">
          {formatDateTime(appointment.appointmentTime)}
        </div>
      )
    },
    {
      key: 'venue',
      title: '场地',
      render: (_, appointment) => (
        <div className="text-sm text-gray-900">
          {appointment.venueName || '未指定场地'}
        </div>
      )
    },
    {
      key: 'status',
      title: '状态',
      render: (_, appointment) => (
        <StatusBadge status={appointment.status} />
      )
    },
    {
      key: 'workOrderStatus',
      title: '工单状态',
      render: (_, appointment) => (
        <span className="text-xs font-medium">{appointment.workOrderStatus}</span>
      )
    },
    {
      key: 'paymentStatus',
      title: '支付状态',
      render: (_, appointment) => {
        const info = getPaymentInfo(appointment);
        return <span className={info.status === '已支付' ? 'text-emerald-600' : 'text-gray-400'}>{info.status}</span>;
      }
    },
    {
      key: 'paidAmount',
      title: '实付金额',
      render: (_, appointment) => {
        const info = getPaymentInfo(appointment);
        return <span>{info.amount !== '-' ? `¥${info.amount}` : '-'}</span>;
      }
    },
    {
      key: 'actions',
      title: '操作',
      render: (_, appointment) => (
        <div className="flex items-center space-x-2">
          <Link
            to={`/appointments/${appointment.id}`}
            className="text-emerald-600 hover:text-emerald-700 text-sm font-medium transition-colors duration-200"
          >
            查看
          </Link>
          {canManageAppointments && (
            <>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => onEdit(appointment)}
                className="text-gray-600 hover:text-gray-700 text-sm font-medium transition-colors duration-200"
              >
                编辑
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => onDelete(appointment.id)}
                className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors duration-200"
              >
                删除
              </button>
            </>
          )}
        </div>
      ),
      cellClassName: 'text-right'
    }
  ];

  return (
    <Table
      columns={columns}
      data={appointments}
      variant="simple"
      className="border-emerald-200/50 bg-white/70 backdrop-blur-sm"
      emptyMessage="没有找到预约记录"
      emptyIcon={
        <svg className="h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      }
    />
  );
};

AppointmentList.propTypes = {
  appointments: PropTypes.array,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  canManageAppointments: PropTypes.bool.isRequired
};

export default AppointmentList;